import { put, select, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects'

import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { callApi } from '@/shared/tools/apiSagaHelper'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'

import { selectBetaFeaturesTools } from './betaFeaturesSelectors'
import {
  cancelRemoval,
  clearSearch,
  fetchFailure,
  fetchRequest,
  fetchSuccess,
  searchFailure,
  searchRequest,
  searchSuccess,
} from './betaFeaturesSlice'
import {
  BetaFeaturesApiError,
  BetaFeaturesAuthData,
  BetaFeaturesListResponse,
  BetaFeaturesRoutes,
  BetaFeaturesState,
  UserSearchResponse,
} from './types'

export const MIN_SEARCH_LENGTH = 3

export const betaFeaturesActions = {
  LOAD: 'BETA_FEATURES/LOAD',
  SEARCH_CANDIDATES: 'BETA_FEATURES/SEARCH_CANDIDATES',
  ADD_TESTER: 'BETA_FEATURES/ADD_TESTER',
  REMOVE_TESTER: 'BETA_FEATURES/REMOVE_TESTER',
}

interface ApiRequest {
  endpoint?: string
  method?: string
  payload?: unknown
  queryParams?: Record<string, string>
  enqueue?: unknown
  showErrorSnackbar?: boolean
  asteriskReplacement?: string
}

const requestApi = callApi as unknown as (request: ApiRequest) => Generator

interface FeatureAction {
  type: string
  feature: string
  enqueue?: unknown
}

interface TesterAction extends FeatureAction {
  userId: number
  onSuccess?: () => void
}

function* betaFeaturesRoutes(): Generator<unknown, BetaFeaturesRoutes, BetaFeaturesAuthData> {
  const authData = yield select(selectAuthData)

  return authData?.extraRoutes?.betaFeatures ?? {}
}

const isApiError = (error: unknown): error is BetaFeaturesApiError => error instanceof Error

const errorMessageOf = (error: unknown): string => {
  if (!isApiError(error)) return 'Unknown error'
  const firstValidationError = Object.values(error.validationErrors ?? {}).flat()[0]

  return firstValidationError ?? error.message
}

export function* loadBetaFeaturesSaga(): Generator {
  try {
    yield put(fetchRequest())
    const routes = (yield* betaFeaturesRoutes()) as BetaFeaturesRoutes
    const response = (yield* requestApi({ endpoint: routes.list })) as BetaFeaturesListResponse
    yield put(fetchSuccess(Array.isArray(response?.data) ? response.data : []))
  } catch (error: unknown) {
    yield put(fetchFailure(errorMessageOf(error)))
  }
}

export function* searchCandidatesSaga({ feature, enqueue }: FeatureAction): Generator {
  const state = (yield select(selectBetaFeaturesTools)) as BetaFeaturesState
  const query = (state.searchQueries[feature] ?? '').trim()
  if (query.length < MIN_SEARCH_LENGTH) return

  try {
    yield put(searchRequest(feature))
    const routes = (yield* betaFeaturesRoutes()) as BetaFeaturesRoutes
    const response = (yield* requestApi({
      endpoint: routes.searchCandidates,
      queryParams: { search: query },
      asteriskReplacement: feature,
      enqueue,
    })) as UserSearchResponse
    yield put(searchSuccess({ feature, users: Array.isArray(response?.data) ? response.data : [] }))
  } catch {
    yield put(searchFailure())
  }
}

export function* addTesterSaga({ feature, userId, enqueue, onSuccess }: TesterAction): Generator {
  try {
    const routes = (yield* betaFeaturesRoutes()) as BetaFeaturesRoutes
    yield* requestApi({
      endpoint: routes.addTesters,
      method: 'POST',
      payload: { user_ids: [userId] },
      asteriskReplacement: feature,
      enqueue,
      showErrorSnackbar: false,
    })
    yield put(clearSearch(feature))
    onSuccess?.()
    yield* loadBetaFeaturesSaga()
  } catch (error: unknown) {
    if (enqueue) showSnackbar(enqueue, { type: SNACKBAR_TYPES.ERROR, message: errorMessageOf(error) })
  }
}

export function* removeTesterSaga({ feature, userId, enqueue, onSuccess }: TesterAction): Generator {
  try {
    const routes = (yield* betaFeaturesRoutes()) as BetaFeaturesRoutes
    yield* requestApi({
      endpoint: routes.removeTester?.replace('+', String(userId)),
      method: 'DELETE',
      asteriskReplacement: feature,
      enqueue,
      showErrorSnackbar: false,
    })
    onSuccess?.()
    yield* loadBetaFeaturesSaga()
  } catch (error: unknown) {
    if (enqueue) showSnackbar(enqueue, { type: SNACKBAR_TYPES.ERROR, message: errorMessageOf(error) })
  } finally {
    yield put(cancelRemoval())
  }
}

export default function* betaFeaturesSagas() {
  yield takeLatest(betaFeaturesActions.LOAD, loadBetaFeaturesSaga)
  yield takeLatest(betaFeaturesActions.SEARCH_CANDIDATES, searchCandidatesSaga)
  yield takeEvery(betaFeaturesActions.ADD_TESTER, addTesterSaga)
  yield takeLeading(betaFeaturesActions.REMOVE_TESTER, removeTesterSaga)
}
